import PropTypes from 'prop-types';
import { BsArrowRepeat } from 'react-icons/bs';

import PipelineStageFields from '#crmFeature/components/leads/PipelineStageFields/PipelineStageFields';
import { usePipelineStageSelection } from '#crmFeature/hooks/usePipelineStageSelection';
import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAuth } from '#globalContexts/AuthProvider';
import { useErrorStore } from '#lib/stores/errorStore';

import { useUpdateContactList } from '../../models/mutations';

const DISABLED_SYNC = { crmSyncPipelineId: null, crmSyncStageId: null, crmSyncUserId: null };

const ContactListCrmSyncModal = ({ isOpen, onClose, list }) => {
	const isActive = Boolean(list.crmSyncPipelineId);
	const selection = usePipelineStageSelection({
		isOpen,
		initialPipelineId: list.crmSyncPipelineId,
		initialStageId: list.crmSyncStageId,
	});

	const { user } = useAuth();
	const updateListMutation = useUpdateContactList();
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const saveSync = async (updates, toast) => {
		try {
			await updateListMutation.mutateAsync({ id: list.id, updates });
			onClose();
			showSuccess(toast);
		} catch {
			// The global MutationCache already shows the error toast.
		}
	};

	const handleSave = () =>
		saveSync(
			{ crmSyncPipelineId: selection.pipelineId, crmSyncStageId: selection.stageId, crmSyncUserId: user.id },
			isActive
				? {
						title: 'CRM-Sync gespeichert',
						message: 'Neue Einträge werden ab jetzt in der gewählten Pipeline und Stage angelegt.',
					}
				: {
						title: 'CRM-Sync aktiviert',
						message: 'Alle Kontakte und Firmen dieser Liste werden im Hintergrund als Deals angelegt.',
					},
		);

	const handleDisable = () =>
		saveSync(DISABLED_SYNC, {
			title: 'CRM-Sync beendet',
			message:
				'Neue Einträge werden nicht mehr automatisch als Deals angelegt. Bestehende Deals bleiben erhalten.',
		});

	if (!isOpen) return null;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="m">
			<Panel.Header icon={BsArrowRepeat} onClose={onClose} subtitle={list.name}>
				{isActive ? 'CRM-Sync bearbeiten' : 'CRM-Sync einrichten'}
			</Panel.Header>
			<Panel.Body>
				<div className="space-y-4">
					<Alert tone="info">
						{isActive
							? 'Neu hinzukommende Kontakte und Firmen werden automatisch als Deals in der gewählten Pipeline und Stage angelegt. Eine Änderung gilt nur für neue Einträge – bestehende Deals bleiben, wo sie sind.'
							: 'Alle Kontakte und Firmen dieser Liste – auch künftig hinzukommende – werden automatisch als Deals in der gewählten Pipeline und Stage angelegt. Firmen, die bereits einen Deal haben, werden übersprungen.'}
					</Alert>
					<PipelineStageFields selection={selection} />
				</div>
			</Panel.Body>
			<Panel.Footer className="items-center">
				{isActive && (
					<Button variant="outline" onClick={handleDisable} disabled={updateListMutation.isPending}>
						Sync beenden
					</Button>
				)}
				<div className="ml-auto flex gap-3">
					<Button variant="outline" onClick={onClose}>
						Abbrechen
					</Button>
					<Button
						variant="solid"
						onClick={handleSave}
						disabled={!selection.isReady || selection.isLoading}
						loading={updateListMutation.isPending}
					>
						{isActive ? 'Speichern' : 'CRM-Sync aktivieren'}
					</Button>
				</div>
			</Panel.Footer>
		</Modal>
	);
};

ContactListCrmSyncModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	list: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
		crmSyncPipelineId: PropTypes.string,
		crmSyncStageId: PropTypes.string,
	}).isRequired,
};

export default ContactListCrmSyncModal;
